import type { ZohoOauthCredentials } from '@kunzite/shared-types';

import type { TokenProvider } from './calls-api';
import { refreshOn401 } from './refresh-helper';

/**
 * F4.5-D2: subset of AuditEventInput from the IS — tenantId is derived
 * server-side from the JWT, so the client never sets it.
 */
export interface AuditEventInput {
  category: 'admin' | 'oauth';
  action: string;
  actorZohouser: string | null;
  target?: Record<string, unknown>;
  before?: Record<string, unknown>;
  after?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
}

export interface AuditClientDeps {
  baseUrl: string;
  tokenProvider: TokenProvider;
  fetch?: typeof globalThis.fetch;
  /**
   * F4.5-D3 fixup: shared closure invoked by `refreshOn401` after a successful
   * /v1/auth/refresh. Mirrors the prop accepted by callsApi/phonebridge so an
   * agent on a long shift doesn't silently lose audit emission once their JWT
   * expires (previously: 401 was treated as a permanent best-effort failure
   * until another client triggered a refresh).
   */
  onJwtUpdate?: (jwt: string, fresh: { expiresAt: string; refreshExpiresAt: string; zohoOauth: ZohoOauthCredentials }) => void;
}

/**
 * Best-effort audit emission to /v1/audit-events. Failures (network down,
 * 401 expired JWT, 4xx bad payload, 5xx server error) are swallowed and
 * logged via console.warn. The orchestrator NEVER blocks on audit.
 *
 * F4.5-D3 fixup: routes the request through `refreshOn401` so an expired JWT
 * is transparently refreshed (matching callsApi/phonebridge/tenants). If the
 * refresh itself fails (refresh token expired, IS down) `refreshOn401` throws,
 * which we still swallow — preserving best-effort semantics.
 */
export class AuditClient {
  private readonly fetchImpl: typeof globalThis.fetch;

  constructor(private readonly deps: AuditClientDeps) {
    this.fetchImpl = deps.fetch ?? globalThis.fetch.bind(globalThis);
  }

  async record(input: AuditEventInput): Promise<void> {
    try {
      const { jwt } = await this.deps.tokenProvider();
      const res = await refreshOn401<Response>(
        {
          baseUrl: this.deps.baseUrl,
          fetch: this.fetchImpl,
          ...(this.deps.onJwtUpdate ? { onJwtUpdate: this.deps.onJwtUpdate } : {}),
        },
        (currentJwt) =>
          this.fetchImpl(`${this.deps.baseUrl}/v1/audit-events`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${currentJwt}`,
            },
            body: JSON.stringify(input),
          }),
        // refreshOn401 only invokes parseSuccess on non-401 responses; we
        // pass through the Response so we can warn on any non-2xx without
        // throwing (best-effort semantics).
        (response) => Promise.resolve(response),
        jwt,
      );
      if (!res.ok) {
        console.warn('[audit-client] non-2xx response (best-effort, continuing)', {
          status: res.status,
          category: input.category,
          action: input.action,
        });
      }
    } catch (err) {
      console.warn('[audit-client] failed (best-effort, continuing)', {
        category: input.category,
        action: input.action,
        err: err instanceof Error ? err.message : err,
      });
    }
  }
}
